import axios from 'axios'
import {
  GET_LOCATIONS_REQUEST,
  GET_LOCATIONS_SUCCESS,
  GET_LOCATIONS_FAILURE,
  SET_USER_ADDRESS_REQUEST,
  SET_USER_ADDRESS_SUCCESS,
  SET_USER_ADDRESS_FAILURE,
  SET_DELIVER_DETAILS,
  CART_ORDER_REQUEST,
  CART_ORDER_FAILURE,
  COUPON_DISCOUNT_REQUEST,
  COUPON_DISCOUNT_SUCCESS,
  COUPON_DISCOUNT_FAILURE,
  RESET_CART,
  SET_POST_CODE_VALIDATION,
  CART_ORDER_TOGGLE_LOADING,
  SET_ORDER_LOADING,
  SET_CREDIT_CARD_ISO_CODE,
  CALCULATE_FEE_SUCCESS,
  CALCULATE_FEE_FAILURE,
  CALCULATE_FEE_REQUEST,
  SET_SERVICE_ADDRESS_DETAILS
} from '../constants'
import { toast } from 'react-toastify'
import { TypedThunk } from 'reducers'
import {
  DeliverDetails,
  DeliveryPlace,
  DeliveryType,
  ProfileInfo,
  RestaurantDetails,
  ServiceTime
} from 'components/common/types'
import {
  CalculatedItems,
  CalculatorItems
} from 'components/common/types/calculatePrice.types'
import { checkIsSpecialBusiness } from 'utils/businessHelpers'
import { format } from 'date-fns'
import { useAppSelector } from 'components/hooks/redux-selectors'

export const getLocations = () => async (dispatch, getState) => {
  try {
    const { businessId } = getState().cart

    dispatch({ type: GET_LOCATIONS_REQUEST })

    const { data } = await axios.get(`/locations_for_business/${businessId}`)

    const islands = data.map((item) => ({ label: item.name, value: item.id }))

    dispatch({
      type: GET_LOCATIONS_SUCCESS,
      payload: {
        islands,
        cities: data
      }
    })
  } catch (error) {
    const errorMessage =
      error?.response?.data?.ExceptionMessage || 'The request is invalid.'

    toast.error(errorMessage, { autoClose: 6000 })

    dispatch({
      type: GET_LOCATIONS_FAILURE,
      payload: errorMessage
    })

    return Promise.reject()
  }
}

export const getAddressByCoordinate =
  (latitude, longitude): TypedThunk<Promise<any>> =>
    async (dispatch, getState) => {
      try {
        dispatch({
          type: SET_USER_ADDRESS_REQUEST
        })

        const { data } = await axios.get(
          `/geo_to_address?latitude=${latitude}&longitude=${longitude}`
        )

        dispatch({
          type: SET_USER_ADDRESS_SUCCESS,
          payload: data.FullAddress
        })

        return data
      } catch (error) {
        const errorMessage =
          error?.response?.data?.ExceptionMessage ||
          'Unable to get your location. Check your location settings and than your location is being shared with your browser or try to deliver somewhere else'

        toast.error(errorMessage, { autoClose: 6000 })

        dispatch({
          type: SET_USER_ADDRESS_FAILURE,
          payload: errorMessage
        })

        return Promise.reject()
      }
    }

export const createCustomPreOrder = 
  (businessId, customerPhone, preorderImage, notes, preorderId) => 
    async (dispatch) => {
      try {
        dispatch({ type: 'CREATE_PREORDER_REQUEST' });
        const formData = new FormData();
        formData.append('businessId', businessId);
        formData.append('customerPhone', customerPhone);
        formData.append('preorderImage', preorderImage); 
        formData.append('notes', notes);
        formData.append('preorderId', preorderId); 
        const { data } = await axios.post(
          `preorder/custom`, 
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        dispatch({ 
          type: 'CREATE_PREORDER_SUCCESS', 
          payload: data 
        });
        return data;
      } catch (error) {
        const errorMessage = 
          error?.response?.data?.ExceptionMessage || 
          'Failed to create order. Please try again.';
        toast.error(errorMessage, { autoClose: 6000 });
        dispatch({
          type: 'CREATE_PREORDER_FAILURE',
          payload: errorMessage,
        });
        return Promise.reject(errorMessage);
      }
};

export const fetchRestaurantInvoiceDetails = async (restaurantSlug: string): Promise<RestaurantDetails> => {
  try {
    const response = await axios.get(`/business/${restaurantSlug}?custom=1`);
    return response.data;
  } catch (error) {
    const errorMessage = "Failed to fetch restaurant details. Please try again.";
    console.error("Error fetching restaurant details:", error);
    toast.error(errorMessage, { autoClose: 6000 });
    throw new Error(errorMessage);
  }
};

export const setDeliverDetails = (deliverDetails: Partial<DeliverDetails>) => ({
  type: SET_DELIVER_DETAILS,
  payload: deliverDetails
})

export const prepareOrderData =
  (creditCart, orderFromCoupon): TypedThunk =>
    async (dispatch, getState) => {
      const {
        businessId,
        items,
        regionId,
        currency,
        isoCode,
        usesTwoDestinations,
        pickupFee,
        merchantId,
        serviceFees,
        usesBillingPayment
      } = getState().cart

      const { data: profileInfo } = getState().auth.profileInfo

      const phoneNumberFromProfile =
        profileInfo?.PhoneNumber ?? profileInfo?.PhoneNumber2

      const { deliverDetails, coupon, needPostCodeValidation, calculatedItems } =
        getState().order

      const { data: user } = getState().auth

      const isIsoCodeExist = isoCode !== null

      const isPickup = deliverDetails?.deliveryType === DeliveryType.PICK_UP
      const isDestinationProfileAddress =
        deliverDetails.destinationAddress.deliveryPlace ===
        DeliveryPlace.PROFILE_ADDRESS
      const isDestinationCoordinate =
        deliverDetails?.destinationAddress?.deliveryPlace ===
        DeliveryPlace.COORDINATE

      const isIsoCodeAndDelivery =
        isIsoCodeExist && !isPickup && !isDestinationProfileAddress

      const deliveryLatitude = isIsoCodeAndDelivery
        ? deliverDetails?.destinationAddress?.googleResponse?.latitude.toString()
        : undefined;

      const deliveryLongitude = isIsoCodeAndDelivery
        ? deliverDetails?.destinationAddress?.googleResponse?.longitude.toString()
        : undefined;

      const customerLatitude = deliveryLatitude || deliverDetails.destinationAddress?.latitude;
      const customerLongitude = deliveryLongitude || deliverDetails.destinationAddress?.longitude;

      const formData = {
        CreateOrderAPIVersion: 6,
        Amount: `${calculatedItems.calculatedTotalPrice}`,
        FeesCollectionId: calculatedItems.data?.FeesCollectionId,
        Currency: currency,
        CustomerContactInfo: {
          deliveryNotes: deliverDetails.deliveryNotes,
          name: deliverDetails.name,
          CustomerLongitude:
            deliveryLongitude || //this contain value when user select different delivery address
            (isDestinationCoordinate ||
              isIsoCodeExist ||
              isDestinationProfileAddress
              ? deliverDetails?.destinationAddress?.longitude
              : ''),
          email: user.UserName,
          address: deliverDetails.additionalInfo,
          CustomerLat:
            deliveryLatitude || //this contain value when user select different delivery address
            (isDestinationCoordinate ||
              isIsoCodeExist ||
              isDestinationProfileAddress
              ? deliverDetails?.destinationAddress?.latitude
              : ''),
          phone: deliverDetails.phoneDialDetails
            ? `${deliverDetails.phoneDialDetails?.dialCode}${deliverDetails.phone}`
            : phoneNumberFromProfile
        },
        DeliveryLat: deliveryLatitude,
        DeliveryLongitude: deliveryLongitude,
        ...(coupon.validCoupon
          ? {
            CouponDiscountAmount:
              typeof coupon.amount === 'number'
                ? Math.abs(coupon.amount)
                : null,
            CouponId: coupon.couponId
          }
          : null),
        AddressFromPreviousOrderUsed: isDestinationProfileAddress,
        IsoCountryUsed: isIsoCodeAndDelivery ? isoCode : undefined,
        GooglePlaceName: isIsoCodeAndDelivery
          ? deliverDetails?.destinationAddress?.googleResponse?.name ?? ''
          : '',
        GooglePlaceFormattedAddress: isIsoCodeAndDelivery
          ? deliverDetails?.destinationAddress?.googleResponse
            ?.formattedAddress ?? ''
          : '',
        GooglePlacePhoneNumber: isIsoCodeAndDelivery
          ? deliverDetails?.destinationAddress?.googleResponse
            ?.formattedPhoneNumber ?? ''
          : '',
        GooglePlaceNamePickup: 'null',
        GooglePlaceFormattedAddressPickup: 'null',
        GooglePlacePhoneNumberPickup: 'null',
        ISOCountryAddressUsed: isIsoCodeAndDelivery,
        IsGoogleApiUsed: isIsoCodeAndDelivery,
        SelectedDeliveryOptionId: isPickup
          ? -1
          : deliverDetails?.finalDeliveryOption?.ID,

        DeliverToDeviceLocation:
          isDestinationCoordinate || isDestinationProfileAddress
            ? 'true'
            : 'false',
        DeliveryInfo: {
          LastName: '',
          IsDelivery: deliverDetails.deliveryType === DeliveryType.DELIVER,
          IsForASAPDelivery: deliverDetails.serviceTime === ServiceTime.ASAP,
          IsPickup: isPickup,
          Address:
            deliverDetails.destinationAddress?.deliveryPlace ===
              DeliveryPlace.COORDINATE
              ? deliverDetails?.destinationAddress?.address
              : `${deliverDetails.destinationAddress?.address} \n ${deliverDetails.additionalInfo}`,
          DeliveryTime:
            deliverDetails.serviceTime === ServiceTime.LATER
              ? format(
                new Date(deliverDetails.deliveryDate),
                "yyyy-MM-dd'T'HH:mm" // yyyy-MM-dd'T'HH:mm
              )
              : null,
          Firstname: deliverDetails.name
        },
        // ...(isPickup
        //   ? null
        //   : // TODO: how will I know that
        //     { DistanceInKilometers: `${deliveryFee.DistanceInKilometers}` }),
        // ...(isPickup
        //   ? null
        //   : // TODO: how will I know that
        //     { DistanceInMiles: `${deliveryFee.DistanceInMiles}` }),
        EmailAddressOfRecipient: user.UserName,
        IsKioskMode: 'false',
        Items: (() => {
          return items.map((item) => {
            const {
              cost,
              itemId,
              itemName,
              optionSetItems,
              quantity,
              itemNotes
            } = item

            const editedOptionSetItems = optionSetItems.map((optionSetItem) => ({
              OptionName: optionSetItem.optionName,
              OptionSet: optionSetItem.optionSet.map((option) => ({
                ItemId: option.id,
                Quantity: option.quantity,
                AdditionalCost: `${option.additionalCost.toFixed(2)}`,
                ItemName: option.itemName
              }))
            }))

            const totalAdditionalCost = editedOptionSetItems.reduce(
              (acc, curr) => {
                let totalOptionSet = 0

                curr.OptionSet.forEach((innerOption) => {
                  totalOptionSet += +innerOption.AdditionalCost
                })

                return acc + totalOptionSet
              },
              0
            )

            return {
              Quantity: quantity,
              ItemName: itemName,
              Cost: `${(cost - totalAdditionalCost).toFixed(2)}`,
              ItemNotes: itemNotes ?? '',
              ItemId: itemId,
              OptionSetItems: [...Array(item.quantity)].map((_, index) => ({
                item: `item ${index + 1}`,
                OptionDetails: editedOptionSetItems
              }))
            }
          })
        })(),
        MerchantIdOfSeller: merchantId,
        OrderInfo: {
          TipAmount: deliverDetails?.tip?.isPercentage
            ? '0.00'
            : Number(deliverDetails?.tip?.amount),
          TipPercent: deliverDetails?.tip?.isPercentage
            ? Number(deliverDetails?.tip?.amount)
            : '0.00',
          OrderNotes: usesBillingPayment
            ? `${items?.[0]?.billingPaymentNote}${deliverDetails.orderNotes
              ? ' \n ' + deliverDetails.orderNotes
              : ''
            }`
            : deliverDetails.orderNotes,
          PickupFee: isPickup ? `${pickupFee}` : '0.00',
          DeliveryFee: isPickup
            ? '0.00'
            : `${deliverDetails.finalDeliveryOption?.CalculatedFee?.toFixed(2)}`,
          Tax: `${deliverDetails.totalTax.toFixed(2)}`
          // TODO: What to do here
          // RestaurantServiceFees: (() => {
          //   if (serviceFees.length) {
          //     return [
          //       { 'CaribeEats Service fee': `${deliverDetails.serviceFee}` },
          //       { TotalRestaurantFees: `${deliverDetails.serviceFee}` }
          //     ]
          //   }
          // })()
        },
        PayknCCInfo: {
          Currency: currency,
          RequestType: '0',
          Comment: 'D3AD04F9-D163-4020-AA7F-9BA02211A474',
          CustomerInfo: {
            CustomerLat: customerLatitude || '',
            Email: user.UserName,
            CustomerLongitude: customerLongitude || '',
            FirstName: deliverDetails.name,
            LastName: null
          },
          CompanyId: '9034',
          CardInfo: (() => {
            if (creditCart) {
              const splitedCreditExpiryDate = creditCart.expiry.split('/')
              const expMonth = splitedCreditExpiryDate[0]
              const expYear = splitedCreditExpiryDate[1]

              return {
                SecurityCode: creditCart.cvc,
                ExpYear: `20${expYear}`,
                CardNumber: creditCart.number,
                NameOnCard: creditCart.name,
                ExpMonth: expMonth
              }
            }

            return {
              SecurityCode: '',
              ExpYear: '',
              CardNumber: '',
              NameOnCard: null,
              ExpMonth: ''
            }
          })(),
          Amount: `${calculatedItems.calculatedTotalPrice}`
        },
        PaymentConfirmationToken: `${Math.floor(Math.random() * 100000000)}`,
        PaymentType: (() => {
          switch (deliverDetails.paymentType) {
            case 'mmg':
              return 'MMG'
            case 'bitcoin':
              return 'BitcoinCash'
            case 'cash':
              return 'CashOnDelivery'
            default:
              return orderFromCoupon && deliverDetails.totalPrice === 0
                ? 'CaribeEatsCredit'
                : 'cctype3'
          }
        })(),
        RestaurantId: businessId,
        locationId: `${regionId}`,
        ...(needPostCodeValidation
          ? {
            BillingAddress: {
              postCode: creditCart.postCode,
              addressLine1: creditCart.billingAddress
            }
          }
          : {}),
        ...(isPickup || usesTwoDestinations
          ? (() => {
            const isDestinationGoogleAddress: boolean =
              !!deliverDetails?.destinationAddress?.googleResponse
            const isPickupGoogleAddress =
              !!deliverDetails?.pickupAddress?.googleResponse
            const isPickupCoordinate =
              deliverDetails?.pickupAddress?.deliveryPlace ===
              DeliveryPlace.COORDINATE
            const isPickupCustom =
              deliverDetails?.pickupAddress?.deliveryPlace ===
              DeliveryPlace.CUSTOM
            const isPickupCustomAndNotWithGoogle =
              isPickupCustom &&
              typeof deliverDetails?.pickupAddress?.googleResponse ===
              'undefined'
            const isDestinationCustomAndNotGoogleAddress: boolean =
              !isDestinationGoogleAddress &&
              deliverDetails?.destinationAddress?.deliveryPlace ===
              DeliveryPlace.CUSTOM

            return {
              DeliveryInfo: {
                Firstname: deliverDetails?.name,
                LastName: '',
                Address: deliverDetails?.pickupAddress?.address,
                IsForASAPDelivery:
                  deliverDetails?.serviceTime === ServiceTime.ASAP && !isPickup,
                DeliveryTime:
                  deliverDetails.serviceTime === ServiceTime.LATER && !isPickup
                    ? format(
                      new Date(deliverDetails.deliveryDate),
                      "yyyy-MM-dd'T'HH:mm"
                    )
                    : null,
                IsDelivery: !isPickup,
                IsPickup: isPickup,
                DestinationAddress: deliverDetails?.destinationAddress?.address
              },
              ...(isPickup ? {
                PickupInfo: {
                  Firstname: deliverDetails?.name,
                  LastName: '',
                  Address: deliverDetails?.pickupAddress?.address,
                  IsForASAPPickup: deliverDetails?.serviceTime === ServiceTime.ASAP,
                  pickupTime: deliverDetails?.serviceTime === ServiceTime.LATER
                    ? format(new Date(deliverDetails.deliveryDate), "yyyy-MM-dd'T'HH:mm")
                    : null,
                  IsDelivery: false,
                  IsPickup: true,
                  DestinationAddress: deliverDetails?.destinationAddress?.address
                }
              } : {}),
              ...(isPickupGoogleAddress && {
                GooglePlaceFormattedAddressPickup:
                  deliverDetails?.pickupAddress?.googleResponse
                    ?.formattedAddress,
                GooglePlaceNamePickup:
                  deliverDetails?.pickupAddress?.googleResponse?.name,
                ISOCountryAddressUsed: true,
                IsGoogleApiUsed: true
              }),
              ...(isDestinationGoogleAddress && {
                GooglePlaceFormattedAddress:
                  deliverDetails?.destinationAddress?.googleResponse
                    ?.formattedAddress,
                GooglePlaceName:
                  deliverDetails?.destinationAddress?.googleResponse?.name,
                ISOCountryAddressUsed: true,
                IsGoogleApiUsed: true
              }),
              DeliverToDeviceLocation:
                isPickupCoordinate || isPickupGoogleAddress,
              PickupFromDeviceLocation:
                isPickupCoordinate || isPickupGoogleAddress,
              SelectedDeliveryOptionId: -1,
              CustomerContactInfo: {
                deliveryNotes: deliverDetails.deliveryNotes,
                name: deliverDetails.name,
                email: user.UserName,
                address: deliverDetails.additionalInfo,
                CustomerLat:
                  isPickupCoordinate || isPickupGoogleAddress
                    ? deliverDetails?.pickupAddress?.latitude
                    : deliverDetails.destinationAddress?.latitude,
                CustomerLongitude:
                  isPickupCoordinate || isPickupGoogleAddress
                    ? deliverDetails?.pickupAddress?.longitude
                    : deliverDetails.destinationAddress?.longitude,
                phone: deliverDetails.phoneDialDetails
                  ? `${deliverDetails.phoneDialDetails?.dialCode}${deliverDetails.phone}`
                  : phoneNumberFromProfile
              }
            }
          })()
          : {})
      }

      return formData
    }

export const order =
  (
    creditCart,
    orderFromCoupon
  ): TypedThunk<Promise<{ isPaymentComplete: boolean; redirectUrl: string }>> =>
    async (dispatch, getState) => {
      const { deliverDetails } = getState().order

      try {
        dispatch({
          type: CART_ORDER_REQUEST
        })

        const isCreditOrder = deliverDetails.paymentType === 'credit'

        const formData = await dispatch(
          prepareOrderData(creditCart, orderFromCoupon)
        )

        if (isCreditOrder) {
          let needPostCodeValidation
          let creditCardValidation

          if (!orderFromCoupon) {
            const splitedCreditExpiryDate = creditCart.expiry.split('/')
            const expMonth = splitedCreditExpiryDate[0]
            const expYear = splitedCreditExpiryDate[1]

            creditCardValidation = await dispatch(
              validateOrder(
                formData.PayknCCInfo.CardInfo.CardNumber,
                expMonth,
                expYear,
                formData.PayknCCInfo.CardInfo.SecurityCode
              )
            )

            needPostCodeValidation = creditCardValidation.data === 1
          }

          if (needPostCodeValidation && creditCart.postCode === '') {
            dispatch({ type: SET_POST_CODE_VALIDATION, payload: true })
            dispatch({
              type: SET_CREDIT_CARD_ISO_CODE,
              payload: creditCardValidation?.country_code
            })
            return Promise.reject()
          }
        }

        dispatch({
          type: CART_ORDER_REQUEST
        })

        const { data } = (await axios.post('/order', formData)) || {}

        return Promise.resolve({
          isPaymentComplete: data?.IsPaymentComplete,
          redirectUrl: data?.RedirectTo
        })
      } catch (err) {
        const errorMessage =
          err?.response?.data?.ExceptionMessage ||
          'There was a problem AFTER your card was charged. Contact us via WhatsApp on +1425 985 5381 for assistance. We look forward to resolving this issue shortly. Order number: 52890474'

        dispatch({
          type: CART_ORDER_FAILURE,
          payload: errorMessage
        })

        toast.error(errorMessage, { autoClose: 6000 })

        return Promise.reject(errorMessage)
      }
    }

export const getCouponDiscount =
  (
    coupon: string,
    amount: number
  ): TypedThunk<Promise<{ amount: number; couponId: string }>> =>
    async (dispatch, getState) => {
      try {
        const {
          businessId,
          currency: businessCurrency,
          subTotal
        } = getState().cart

        dispatch({
          type: COUPON_DISCOUNT_REQUEST
        })

        const formData = {
          coupon,
          amount,
          business_id: businessId,
          subTotal
        }

        const { data } = await axios.post('/coupon', formData)

        if (data.status) {
          dispatch({
            type: COUPON_DISCOUNT_SUCCESS,
            payload: {
              amount: data?.Data?.Amount,
              couponId: data?.Data?.CouponId
            }
          })

          toast.info(
            <div style={{ padding: '10px 0' }}>
              <div>
                Current coupon amount:
                {` ${data.Data.Currency} ${data.BalanceBeforeUse}`}
              </div>
              <br />
              <div>
                Amount being used now:
                {` ${businessCurrency} ${data.Data.Amount}`}
              </div>
              <br />
              <div>
                Amount remaning after this order:
                {` ${data.Data.Currency} ${data.BalanceAfterUse}`}
              </div>
              <br />
              <div>
                You can use the
                {` ${data.Data.Currency} ${data.BalanceAfterUse} `}
                on another order
              </div>
            </div>,
            { autoClose: 7000 }
          )

          if (
            amount - data?.Data?.Amount < 2 &&
            amount - data?.Data?.Amount !== 0
          ) {
            toast.error(
              "We can't process a payment for less than 2.00. Buy another item or add a tip.",
              {
                autoClose: 7000
              }
            )

            return Promise.reject({
              type: 'ADD_MORE',
              amount: data?.Data?.Amount,
              couponId: data?.Data?.CouponId
            })
          }

          return {
            amount: data?.Data?.Amount,
            couponId: data?.Data?.CouponId
          }
        } else {
          dispatch({
            type: COUPON_DISCOUNT_FAILURE,
            payload: data.Message
          })

          toast.error(data.Message, { autoClose: 6000 })

          return Promise.reject({})
        }
      } catch (error) {
        dispatch({
          type: COUPON_DISCOUNT_FAILURE,
          payload: error?.response?.data?.Message
        })

        toast.error(error?.response?.data?.Message || 'The request is invalid.', {
          autoClose: 7000
        })

        return Promise.reject(error)
      }
    }

export const validateOrder =
  (cardNumber, expireMonth, expireYear, cvc) => async (dispatch, getState) => {
    try {
      const res = await axios.post('/card/check', {
        number: cardNumber,
        digits: cardNumber.replace(' ', '').slice(0, 6),
        exp_month: expireMonth,
        exp_year: `20${expireYear}`,
        cvc
      })

      dispatch({ type: CART_ORDER_TOGGLE_LOADING })

      return res.data
    } catch (error) {
      const errorMessage =
        error?.response?.data?.ExceptionMessage || 'The request is invalid.'

      toast.error(errorMessage, { autoClose: 6000 })
    }
  }

export const resetCart = (): TypedThunk<void> => async (dispatch, getState) => {
  const serviceDeliveryOptions = getState().serviceDeliveryOptions

  dispatch({ type: RESET_CART, payload: serviceDeliveryOptions })
}

export const setOrderLoading = (value) => ({
  type: SET_ORDER_LOADING,
  payload: value
})

export const prepareRideShareCalculateFeeData =
  (): TypedThunk<any> => (dispatch, getState) => {
    const { deliverDetails, coupon } = getState().order
    const { businessId, regionId, subTotal, isoCode, items } = getState().cart

    const isDestinationGoogleAddress: boolean =
      !!deliverDetails?.destinationAddress?.googleResponse
    const isPickupGoogleAddress =
      !!deliverDetails?.pickupAddress?.googleResponse
    const isPickupCoordinate =
      deliverDetails?.pickupAddress?.deliveryPlace === DeliveryPlace.COORDINATE
    const isPickupCustom =
      deliverDetails?.pickupAddress?.deliveryPlace === DeliveryPlace.CUSTOM
    const isPickupCustomAndNotWithGoogle =
      isPickupCustom &&
      typeof deliverDetails?.pickupAddress?.googleResponse === 'undefined'
    const isDestinationCustomAndNotGoogleAddress: boolean =
      !isDestinationGoogleAddress &&
      deliverDetails?.destinationAddress?.deliveryPlace === DeliveryPlace.CUSTOM

    const formData = {
      Address1: undefined,
      ...(isPickupGoogleAddress && {
        Address2:
          deliverDetails?.pickupAddress?.googleResponse?.formattedAddress,
        PickupLatitude: deliverDetails?.pickupAddress?.googleResponse?.latitude,
        PickupLongitude:
          deliverDetails?.pickupAddress?.googleResponse?.longitude,
        DeliverToDeviceLocation: true,
        GooglePlaceFormattedAddress:
          deliverDetails?.pickupAddress?.googleResponse?.formattedAddress,
        GooglePlaceName: deliverDetails?.pickupAddress?.googleResponse?.name,
        ISOCountryAddressUsed: true
      }),
      ...(isPickupCustomAndNotWithGoogle && {
        CityTownOrVillage: deliverDetails?.pickupAddress?.town,
        Parish: deliverDetails?.pickupAddress?.parish,
        DeliverToDeviceLocation: false,
        isPickupCustomAndNotWithGoogle: true,
        PickupAddress: deliverDetails?.pickupAddress?.address,
        Address2: 'Pickup'
      }),
      ...(isPickupCoordinate && {
        PickupAddress: deliverDetails?.pickupAddress?.address,
        PickupFromDeviceLocation: true,
        PickupLatitude: deliverDetails?.pickupAddress?.latitude,
        PickupLongitude: deliverDetails?.pickupAddress?.longitude,
        DeliverToDeviceLocation: true,
      }),
      ...(isDestinationGoogleAddress && {
        DestinationAddress:
          deliverDetails?.destinationAddress?.googleResponse?.formattedAddress,
        DeliveryLat: deliverDetails?.destinationAddress?.googleResponse?.latitude,
        DeliveryLongitude:
          deliverDetails?.destinationAddress?.googleResponse?.longitude,
      }),
      ...(isDestinationCustomAndNotGoogleAddress && {
        DestinationAddress: deliverDetails?.destinationAddress?.address
      }),
      IsGoogleApiUsed: isDestinationGoogleAddress,
      PickupFromDeviceLocation: isPickupCoordinate,
      PostalCode: isoCode,
      RestaurantId: businessId,
      RestaurantLocationId: `${regionId}`,
      Subtotal: subTotal,
      Items: items,
      CouponId: coupon?.validCoupon ? coupon?.couponId : null,
      IsPickupOrder: deliverDetails?.deliveryType === DeliveryType.PICK_UP,
      DeliveryTypeId: deliverDetails?.finalDeliveryOption?.ID,
      TipAmount: deliverDetails?.tip?.isPercentage
        ? 0
        : +deliverDetails?.tip?.amount,
      TipPercent: deliverDetails?.tip?.isPercentage
        ? +deliverDetails?.tip?.amount
        : 0
    }

    return formData
  }

export const prepareSingleDestinationData =
  (
    profileInfo?: ProfileInfo | null
  ): TypedThunk<any> => (dispatch, getState) => {
    const { deliverDetails, coupon } = getState().order
    const { businessId, regionId, subTotal, isoCode, items } = getState().cart


    console.log("profile ", profileInfo)
    const isDestinationGoogleAddress: boolean =
      !!deliverDetails?.destinationAddress?.googleResponse
    const isDestinationCustomAndNotGoogleAddress: boolean =
      !isDestinationGoogleAddress &&
      deliverDetails?.destinationAddress?.deliveryPlace === DeliveryPlace.CUSTOM
    const isDestinationCoordinate =
      deliverDetails?.destinationAddress?.deliveryPlace ===
      DeliveryPlace.COORDINATE

    const isDestinationProfileAddress =
      deliverDetails?.destinationAddress?.deliveryPlace ===
      DeliveryPlace.PROFILE_ADDRESS

    const isPickup = deliverDetails?.deliveryType === DeliveryType.PICK_UP

    const formData = {
      ...(isDestinationGoogleAddress && {
        Address2:
          deliverDetails?.destinationAddress?.googleResponse?.formattedAddress,
        DeliveryLat:
          deliverDetails?.destinationAddress?.googleResponse?.latitude,
        DeliveryLongitude:
          deliverDetails?.destinationAddress?.googleResponse?.longitude,
        DeliverToDeviceLocation: true,
        GooglePlaceFormattedAddress:
          deliverDetails?.destinationAddress?.googleResponse?.formattedAddress,
        GooglePlaceName:
          deliverDetails?.destinationAddress?.googleResponse?.name,
        PickupLatitude:
          deliverDetails?.destinationAddress?.googleResponse?.latitude,
        PickupLongitude:
          deliverDetails?.destinationAddress?.googleResponse?.longitude,
        ISOCountryAddressUsed: true
      }),
      ...(isDestinationCustomAndNotGoogleAddress && {
        Address2: deliverDetails?.destinationAddress?.address,
        CityTownOrVillage: deliverDetails?.destinationAddress?.town,
        Parish: deliverDetails?.destinationAddress?.parish
      }),
      ...(isDestinationCoordinate && {
        DeliverToDeviceLocation: true,
        DeliveryLat: deliverDetails?.destinationAddress?.latitude,
        DeliveryLongitude: deliverDetails?.destinationAddress?.longitude
      }),
      DeliverToDeviceLocation: isDestinationCoordinate,
      ...(isDestinationProfileAddress && {
        Address2: deliverDetails?.destinationAddress?.address,
        DeliverToDeviceLocation: true,
        DeliveryLat: deliverDetails?.destinationAddress?.latitude || profileInfo?.customerAddresses?.[0]?.Latitude,
        DeliveryLongitude: deliverDetails?.destinationAddress?.longitude || profileInfo?.customerAddresses?.[0]?.Longitude,
        AddressFromPreviousOrderUsed: true
      }),
      IsGoogleApiUsed: isDestinationGoogleAddress,
      PostalCode: isDestinationGoogleAddress ? isoCode : undefined,
      RestaurantId: businessId,
      RestaurantLocationId: `${regionId}`,
      Subtotal: subTotal,
      Items: items,
      CouponId: coupon?.validCoupon ? coupon?.couponId : null,
      IsPickupOrder: isPickup,
      DeliveryTypeId: deliverDetails?.finalDeliveryOption?.ID,
      TipAmount: deliverDetails?.tip?.isPercentage
        ? 0
        : +deliverDetails?.tip?.amount,
      TipPercent: deliverDetails?.tip?.isPercentage
        ? +deliverDetails?.tip?.amount
        : 0
    }

    return formData
  }

export const calculatePrice =
  (
    calculatorItems?: Partial<CalculatorItems>,
    profileInfo?: ProfileInfo | null
  ): TypedThunk<
    Promise<{ calculatedItems: CalculatedItems; calculatedTotalPrice: number }>
  > =>
    async (dispatch, getState) => {
      try {
        dispatch({
          type: CALCULATE_FEE_REQUEST
        })

        const { calculatedItems } = getState().order

        const { usesTwoDestinations } = getState().cart

        const preparedData = dispatch(
          usesTwoDestinations
            ? prepareRideShareCalculateFeeData()
            : prepareSingleDestinationData(profileInfo)
        )

        for (const property in calculatorItems) {
          if (property === 'tip') {
            preparedData.TipAmount = calculatorItems?.tip?.isPercentage
              ? 0
              : Number(calculatorItems?.tip?.amount)

            preparedData.TipPercent = calculatorItems?.tip?.isPercentage
              ? Number(calculatorItems?.tip?.amount)
              : 0
          }

          if (property === 'finalDeliveryOption') {
            preparedData.DeliveryTypeId = calculatorItems?.finalDeliveryOption?.ID
          }

          if (property === 'couponId') {
            preparedData.CouponId = calculatorItems?.couponId
          }
        }

        preparedData.FeesCollectionId =
          calculatedItems.data?.FeesCollectionId ?? null

        const { data: calculatedResponse } = await axios.post(
          '/calculate_fees',
          preparedData
        )

        const calculatedTotalPrice =
          calculatedResponse?.Fees?.[calculatedResponse?.Fees?.length - 1]?.Amount

        dispatch({
          type: CALCULATE_FEE_SUCCESS,
          payload: {
            calculatedItems: calculatedResponse,
            calculatedTotalPrice: calculatedTotalPrice.toFixed(2)
          }
        })

        return {
          calculatedTotalPrice,
          calculatedItems: calculatedResponse
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.ExceptionMessage ||
          error?.response?.data?.Message ||
          'The request is invalid.'

        toast.error(errorMessage, { autoClose: 6000 })

        dispatch({
          type: CALCULATE_FEE_FAILURE,
          payload: errorMessage
        })

        return Promise.reject(errorMessage)
      }
    }

export const setServiceAddressDetails =
  (): TypedThunk<void> => async (dispatch, getState) => {
    const serviceDeliveryOptions = getState().serviceDeliveryOptions
    const {
      isAuthenticated,
      profileInfo: { data: profileInfo }
    } = getState().auth

    dispatch({
      type: SET_SERVICE_ADDRESS_DETAILS,
      payload: {
        ...(() => {
          if (profileInfo?.customerAddresses?.length && isAuthenticated) {
            const firstAddressInProfileInfo =
              profileInfo?.customerAddresses?.[0]

            return {
              town: '',
              parish: '',
              parishTown: '',
              island: '',
              googleResponse: undefined,
              address: firstAddressInProfileInfo?.Address,
              deliveryPlace: DeliveryPlace.PROFILE_ADDRESS,
              filteredParishes: [],
              streetDetails: '',
              latitude: firstAddressInProfileInfo?.Latitude
                ? +firstAddressInProfileInfo?.Latitude
                : undefined,
              longitude: firstAddressInProfileInfo?.Longitude
                ? +firstAddressInProfileInfo?.Longitude
                : undefined
            }
          } else {
            return serviceDeliveryOptions.destinationAddress
          }
        })()
      }
    })
  }

export const syncOptions =
  ({ businessData }): TypedThunk<void> =>
    async (dispatch, getState) => {
      const serviceDeliveryOptions = getState().serviceDeliveryOptions
      const { isAuthenticated } = getState().auth

      const cart = getState().cart

      if (!checkIsSpecialBusiness) {
        dispatch(
          setDeliverDetails({
            deliveryType: null,
            serviceTime: null,
            deliveryDate: ''
          })
        )
      }

      // Firstly we check if there is any item in the cart
      // If there is items then we check cart's delivery and pickup status
      // IF there is not any item check business page delivery and pickup status
      // if it is not special business set to default valeus for options.
      if (checkIsSpecialBusiness(cart.businessTypeId) && isAuthenticated) {
        const isPickupAvailableInCartBusiness = cart.pickup
        const isDeliveryAvailableInCartBusiness = cart.delivery

        dispatch(
          setDeliverDetails({
            deliveryDate: serviceDeliveryOptions.deliveryDate,
            serviceTime: serviceDeliveryOptions.serviceTime,
            destinationAddress: serviceDeliveryOptions.destinationAddress,
            // Set these values only when business accepts pick_up or deliver
            ...(isPickupAvailableInCartBusiness &&
              serviceDeliveryOptions.deliveryType === DeliveryType.PICK_UP && {
              deliveryType: DeliveryType.PICK_UP
            }),
            ...(isDeliveryAvailableInCartBusiness &&
              serviceDeliveryOptions.deliveryType === DeliveryType.DELIVER && {
              deliveryType: DeliveryType.DELIVER
            })
          })
        )
      }

      if (
        !cart.businessId &&
        checkIsSpecialBusiness(businessData?.business_type_id) &&
        isAuthenticated
      ) {
        const isPickupAvailableInCurrentBusiness = businessData.pickup
        const isDeliveryAvailableForCurrentBusiness = businessData.delivery

        dispatch(
          setDeliverDetails({
            deliveryDate: serviceDeliveryOptions.deliveryDate,
            serviceTime: serviceDeliveryOptions.serviceTime,
            destinationAddress: serviceDeliveryOptions.destinationAddress,
            // Set these values only when business accepts pick_up or deliver
            ...(isPickupAvailableInCurrentBusiness &&
              serviceDeliveryOptions.deliveryType === DeliveryType.PICK_UP && {
              deliveryType: DeliveryType.PICK_UP
            }),
            ...(isDeliveryAvailableForCurrentBusiness &&
              serviceDeliveryOptions.deliveryType === DeliveryType.DELIVER && {
              deliveryType: DeliveryType.DELIVER
            })
          })
        )
      }
    }
